import { EditIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { Link } from 'react-scroll';
import {
  HotelFragmentFragment,
  OfferForFrontendItemFragmentFragment,
  OfferItemFragmentFragment,
  RatingConnection,
} from '../../../generated/types';
import useHotelMapMarkers from '../../../hooks/useHotelMapMarkers';
import { useStore } from '../../../store/provider';
import { HTMLText } from '../../Common/HTMLText';
import PageImagesEbikeAtlas, {
  SliderImage,
} from '../../Common/PageImagesEbikeAtlas';
import Container from '../../Layout/Container';
import ContactPopoverButton from '../../Node/Hotel/ContactPopoverButton';
import { CTAStack } from '../../Node/Hotel/EbikeAtlasHotelIndex';
import HotelRatingList from '../../Node/Hotel/HotelRatingList';
import HotelTestimonial from '../../Node/Hotel/HotelTestimonial';
import RatingStars from '../../Node/Hotel/RatingStars';
import FeatureListGrouped from '../../Node/Hotel/FeatureListGrouped';
import React, { useMemo, useState } from 'react';
import ImageTextTeaserBase from '../../Common/ImageTextTeaserBase';
import getIconForCategoryKey from '../../../utils/getIconForCategoryKey';
import TeaserGrid from '../../Common/TeaserGrid';
import dynamic from 'next/dynamic';
import { LocationIcon } from '../../../config/icons';
import { FacebookProvider, Page } from 'react-facebook';
import OfferTeaser from '../../Node/Hotel/OfferTeaser';
import getAppComponent from '../../../utils/getAppComponent';
import ProductTeaser from '../../Node/Product/ProductTeaser';
import useMergedHotelFeatures from '../../../hooks/useMergedHotelFeatures';
import HotelOfferModal from '../../Node/Hotel/HotelOfferModal';
import { ProductListGridSlider } from '../../Common/ProductListGridSlider';
import { FaExternalLinkAlt } from '@react-icons/all-files/fa/FaExternalLinkAlt';
import { StaticAdSlot } from '../../Common/StaticAdSlot';

const HotelMap = dynamic(() => import('../../Common/HotelMap'), {
  ssr: false,
});

interface HotelDetailProps {
  data: HotelFragmentFragment;
  setShowRatingModal: (val: boolean) => void;
  setShowBookingModal: (val: boolean) => void;
}

const HotelContent: React.FC<HotelDetailProps> = ({
  data,
  setShowRatingModal,
  setShowBookingModal,
}) => {
  const store = useStore();
  const [
    activeOfferModal,
    setActiveOfferModal,
  ] = useState<OfferForFrontendItemFragmentFragment>(null);

  const images: SliderImage[] = [data.teaserImage] as SliderImage[];
  const mapMarkers = useHotelMapMarkers(data);

  if (data.detailsForFrontend && data.detailsForFrontend.images) {
    data.detailsForFrontend.images.forEach((image: any) => {
      images.push(image);
    });
  }

  const activitiesToSearch = ['motorcycle', 'tour', 'pass'];
  const activities = data.activities?.filter((activity) =>
    activitiesToSearch.includes(activity.category.key)
  );

  let renderPriceInfoColumn = useMemo(() => {
    let render = false;
    data.rooms.forEach((room) => {
      room.prices.forEach((price) => {
        if (price.description && price.description !== '') {
          render = true;
        }
      });
    });

    return render;
  }, [data.rooms]);

  const features = useMergedHotelFeatures(data);

  return (
    <Box>
      {activeOfferModal && (
        <HotelOfferModal
          data={activeOfferModal}
          isOpen={true}
          onClose={() => setActiveOfferModal(null)}
        />
      )}
      <Container paddingY={4}>
        <CTAStack
          setShowRatingModal={setShowRatingModal}
          setShowBookingModal={setShowBookingModal}
          data={data}
          display={{
            base: 'flex',
            md: 'none',
          }}
          mb={{
            base: 8,
            md: 0,
          }}
        />

        {/* <StaticAdSlot adKey="leaderboard" /> */}

        <Flex
          flexDirection={{
            base: 'column',
            md: 'column',
            lg: 'row',
          }}
        >
          <Box flex={1}>
            <Heading>{data.title}</Heading>
            {data.detailsForFrontend && (
              <Box mt={4} fontSize="lg">
                <HTMLText text={data.detailsForFrontend?.body} />
              </Box>
            )}

            {/* <StaticAdSlot adKey="in_content_2" /> */}

            <Flex
              flex={1}
              mt={8}
              flexDirection={{
                base: 'column',
                md: 'row',
              }}
            >
              <Box flex={1}>
                <Heading mb={8}>Kontakt</Heading>
                <VStack spacing={4}>
                  {!data.onlyShowOnlineBooking && (
                    <ContactPopoverButton {...data} />
                  )}
                  {!data.onlyShowOnlineBooking && (
                    <Button
                      width="100%"
                      onClick={() => setShowBookingModal(true)}
                    >
                      Buchungsanfrage
                    </Button>
                  )}
                  {data.onlineBookingUrl && (
                    <Button
                      width="100%"
                      as="a"
                      href={data.onlineBookingUrl}
                      target="_blank"
                    >
                      Online buchen <Icon ml={2} as={FaExternalLinkAlt} />
                    </Button>
                  )}
                </VStack>
              </Box>
              <Box
                marginLeft={{
                  base: 0,
                  md: 8,
                }}
                mt={{
                  base: 8,
                  md: 0,
                }}
                flex={1}
              >
                <Heading mb={8}>Bewertungen</Heading>
                <RatingStars
                  title="Verpflegung"
                  value={data.averageRating!.catering}
                />
                <RatingStars
                  title="Unterkunft"
                  value={data.averageRating!.housing}
                />
                <RatingStars
                  title="Freundlichkeit"
                  value={data.averageRating!.kindness}
                />
                <Link to="bewertungen" smooth>
                  <Button mt={4} width="100%">
                    Bewertungen ansehen
                  </Button>
                </Link>
              </Box>
            </Flex>
          </Box>
          <Box
            flex="0 0 auto"
            width={{
              lg: '500px',
            }}
            mt={{
              base: 12,
              md: 0,
            }}
            ml={{
              base: 0,
              md: 12,
            }}
          >
            {features && features.length > 0 && (
              <Box>
                <Heading mb={4}>Ausstattung</Heading>

                <FeatureListGrouped features={features} />
              </Box>
            )}

            {/* <StaticAdSlot adKey="sidebar" /> */}
          </Box>
        </Flex>
      </Container>

      {images.length > 0 && (
        <Box mt={12}>
          <Container>
            <Heading>Fotos</Heading>

            <PageImagesEbikeAtlas images={images} />
          </Container>
        </Box>
      )}

      {/* <Container>
        <StaticAdSlot adKey="in_content_3" />
      </Container> */}

      {(data.rooms.length > 0 || data.offersForFrontend?.length > 0) && (
        <Box mt={12}>
          <Container smaller={Boolean(!data.facebookPageUrl)}>
            <Flex
              flexDirection={{
                base: 'column',
                md: 'column',
                lg: 'row',
              }}
            >
              <Box flex={1}>
                <Heading>Preise</Heading>
                <Box
                  maxW="100%"
                  whiteSpace="nowrap"
                  overflowY="hidden"
                  overflowX="auto"
                >
                  <Table>
                    <Thead>
                      <Tr>
                        <Th fontSize="lg">Raumtyp</Th>
                        <Th fontSize="lg">Preis</Th>
                        {renderPriceInfoColumn && <Th fontSize="lg">Info</Th>}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {data.rooms.map((room, i) => (
                        <React.Fragment key={i}>
                          <Tr key={i}>
                            <Td>{room.roomType.name}</Td>
                            <Td>
                              ab{' '}
                              {room.prices.length > 0
                                ? room.prices[0].price
                                : ''}{' '}
                              €
                            </Td>
                            {renderPriceInfoColumn && (
                              <Td>
                                {room.prices.length > 0
                                  ? room.prices[0].description
                                  : ''}
                              </Td>
                            )}
                          </Tr>
                          {room.prices.slice(1).map((price, i) => (
                            <Tr key={i}>
                              <Td></Td>
                              <Td>ab {price.price} €</Td>
                              {renderPriceInfoColumn && (
                                <Td>{price.description}</Td>
                              )}
                            </Tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </Tbody>
                  </Table>
                </Box>

                {data.offersForFrontend?.length > 0 && (
                  <Box my={9}>
                    <Heading mb={6}>Angebote</Heading>

                    <TeaserGrid columns={2}>
                      {data.offersForFrontend.map((offer) => (
                        <Box key={offer.id}>
                          <OfferTeaser
                            noCta
                            data={offer as OfferItemFragmentFragment}
                          >
                            <Button
                              mt={4}
                              onClick={() => setActiveOfferModal(offer)}
                            >
                              read more
                            </Button>
                          </OfferTeaser>
                        </Box>
                      ))}
                    </TeaserGrid>
                  </Box>
                )}
              </Box>

              {data.facebookPageUrl && (
                <Box
                  flex="0 0 auto"
                  width={{
                    lg: '412px',
                  }}
                  mt={{
                    base: 12,
                    md: 0,
                  }}
                  ml={{
                    base: 0,
                    md: 12,
                  }}
                >
                  <FacebookProvider appId="302504163426957">
                    <Page tabs="timeline" href={data.facebookPageUrl} />
                  </FacebookProvider>
                </Box>
              )}
            </Flex>
          </Container>
        </Box>
      )}

      {activities?.length > 0 && (
        <>
          <Container mt={8}>
            <Heading mb={8}>
              Eigene{' '}
              <Box as="span" color="textColor">
                Motorradtouren
              </Box>{' '}
              des Hotels
            </Heading>

            <Box my={8}>
              <TeaserGrid>
                {activities.map((t) => {
                  const Icon = getIconForCategoryKey(t.category.key);

                  return (
                    <Box key={t.id}>
                      <ImageTextTeaserBase
                        linkText="Check it now"
                        url={t.url}
                        image={t.teaserImage?.teaserTextImage?.url}
                        my={0}
                        mx={0}
                        bg={`rgba(0, 0, 0, .65)`}
                        padding={{
                          base: 3,
                          md: 8,
                        }}
                      >
                        <Flex alignItems="center">
                          <Box
                            alignSelf="flex-start"
                            padding={2}
                            borderRadius={{
                              base: 0,
                              md: 'lg',
                            }}
                            display="inline-flex"
                            fontSize={{
                              base: 'xl',
                              lg: '3xl',
                            }}
                            border="2px solid"
                            borderColor="textColor"
                            color="#fff"
                          >
                            <Icon />
                          </Box>
                          <Box ml={4} fontSize="md" fontWeight="bold">
                            <Box>{t.country?.name}</Box>
                            <Box>{t.region?.name}</Box>
                          </Box>
                        </Flex>
                        <Box
                          mt={{
                            base: 0,
                            md: 4,
                          }}
                        >
                          {t.length > 0 && (
                            <Heading
                              mt={4}
                              fontSize={{
                                base: 'xl',
                                md: '2xl',
                              }}
                            >
                              {t.length} KM
                            </Heading>
                          )}
                          <Box
                            marginY={2}
                            width="60px"
                            height="2px"
                            bg="brand.600"
                          />
                          <Flex alignItems="center" mt={4} fontSize="xl">
                            <LocationIcon color="textColor" mr={2} />{' '}
                            GPS-Download
                          </Flex>
                          <Heading
                            mt={4}
                            fontSize={{
                              base: 'lg',
                              lg: 'xl',
                            }}
                          >
                            {t.title}
                          </Heading>
                          <HTMLText mt={4} text={t.body} truncate={110} />
                        </Box>
                      </ImageTextTeaserBase>
                    </Box>
                  );
                })}
              </TeaserGrid>
            </Box>
          </Container>
        </>
      )}

      {data.location && (
        <Box mt={12}>
          <HotelMap
            scrollWheelZoomDelayed
            scrollWheelZoom={false}
            data={data}
            markers={mapMarkers}
            center={[data.location!.lat, data.location!.lng]}
          />
        </Box>
      )}

      {/* <Container>
        <StaticAdSlot adKey="in_content_4" />
      </Container> */}

      {data.pushedRatings?.total > 0 && (
        <Box mt={12}>
          <HotelTestimonial
            image={data.testimonialImage || data.teaserImage}
            rating={data.pushedRatings.items?.[0]}
            darken={!data.testimonialImage}
            showCallToAction={data.ratings?.total > 1}
          />
        </Box>
      )}

      {data.activitiesInRegion && data.activitiesInRegion?.total > 0 && (
        <>
          <Container mt={8}>
            <Heading mb={8}>Motorradtouren in der Region</Heading>

            <Box my={8}>
              <TeaserGrid columns={1} spacing={16}>
                {data.activitiesInRegion.items.map((activity) => {
                  const TeaserComponent = getAppComponent(
                    process.env.APP_ID!,
                    store.config.usesNewLayout,
                    'ActivityTeaser'
                  );

                  return <TeaserComponent key={activity!.id} data={activity} />;
                })}
              </TeaserGrid>
            </Box>
          </Container>
        </>
      )}

      <Box mt={12} id="bewertungen">
        <Container smaller>
          <Flex
            alignItems="center"
            justifyContent="space-between"
            direction={{
              base: 'column',
              md: 'row',
            }}
          >
            <Heading>Bewertungen</Heading>

            <Box>
              <Button onClick={() => setShowRatingModal(true)}>
                <EditIcon marginRight={2} />
                Bewertung schreiben
              </Button>
            </Box>
          </Flex>

          <HotelRatingList
            hotel={data}
            data={data.ratings! as RatingConnection}
          />
        </Container>
      </Box>

      {data.productsInRegion && data.productsInRegion?.total > 0 && (
        <>
          <Container mt={8}>
            <Box my={8}>
              <ProductListGridSlider
                title="Produktgalerie"
                items={data.productsInRegion.items}
              />
            </Box>
          </Container>
        </>
      )}
    </Box>
  );
};

export default HotelContent;
